<template>
  <div v-if="mapUnavailable" class="listing-map-placeholder">
    <SvgIcon name="map" :size="32" />
    <p class="listing-map-placeholder__label">{{ t('listing-map.unavailable') }}</p>
  </div>
  <div
    v-else
    class="listing-map"
    :class="{ 'has-card': hasSelection, 'listing-map--narrow': narrowMap, 'listing-map--fullscreen': fullscreen }"
    :style="cardStyle"
  >
    <div ref="container" class="listing-map__canvas" />
    <div class="listing-map-legend" :class="{ 'is-open': legendOpen }">
      <button
        type="button"
        class="listing-map-legend__toggle"
        :aria-label="legendOpen ? t('listing-map.legend.hide') : t('listing-map.legend.show')"
        :aria-expanded="legendOpen"
        @click="legendOpen = !legendOpen"
      >
        <SvgIcon name="list" :size="16" class="listing-map-legend__icon" />
        {{ t('listing-map.legend.title') }}
        <SvgIcon name="chevron" :size="13" class="listing-map-legend__chevron" />
      </button>
      <div v-if="legendOpen" class="listing-map-legend__rows">
        <span class="listing-map-legend__row">
          <svg class="listing-map-legend__pin" :viewBox="PIN_VIEWBOX" aria-hidden="true">
            <g transform="translate(0.300671, 0.128358)">
              <path :d="PIN_BODY_PATH" :fill="MARKER_COLOR" />
              <g transform="translate(17, 22)">
                <path :d="PIN_W_PATH" fill="#ffffff" />
              </g>
            </g>
          </svg>
          {{ t('listing-map.legend.location') }}
        </span>
        <span class="listing-map-legend__row">
          <svg class="listing-map-legend__cluster" viewBox="0 0 72 72" aria-hidden="true">
            <circle cx="36" cy="38" r="25" :fill="MARKER_SHADOW" />
            <circle cx="36" cy="36" r="24" :fill="CLUSTER_FILL" :stroke="CLUSTER_STROKE" stroke-width="3" />
            <text x="36" y="42" text-anchor="middle" :fill="CLUSTER_STROKE" font-size="20" font-weight="700">8</text>
          </svg>
          {{ t('listing-map.legend.cluster') }}
        </span>
        <span class="listing-map-legend__row">
          <svg class="listing-map-legend__pin" :viewBox="PIN_VIEWBOX" aria-hidden="true">
            <g transform="translate(0.300671, 0.128358)">
              <clipPath :id="UPCOMING_MARKER_CLIP_ID">
                <path :d="PIN_BODY_PATH" />
              </clipPath>
              <path
                :d="PIN_BODY_PATH"
                :fill="UPCOMING_MARKER_FILL"
                :stroke="MARKER_COLOR"
                :stroke-width="UPCOMING_MARKER_EDGE_WIDTH * 2"
                :clip-path="`url(#${UPCOMING_MARKER_CLIP_ID})`"
              />
              <g transform="translate(17, 22)">
                <path :d="PIN_W_PATH" :fill="MARKER_COLOR" />
              </g>
            </g>
          </svg>
          {{ t('listing-map.legend.upcoming') }}
        </span>
      </div>
    </div>

    <button
      type="button"
      class="listing-map-fullscreen"
      :aria-label="fullscreen ? t('listing-map.fullscreen.exit') : t('listing-map.fullscreen.enter')"
      :aria-pressed="fullscreen"
      @click="toggleFullscreen"
    >
      <SvgIcon :name="fullscreen ? 'collapse' : 'expand'" :size="18" :stroke="2" />
    </button>

    <MapCard
      v-if="hasSelection"
      ref="card"
      :index="selection.index"
      :total="selection.items.length"
      :label="t('listing-map.card.aria-label')"
      @prev="step(-1)"
      @next="step(1)"
      @close="onCardClose"
      @resize="onCardResize"
    >
      <ListingMapItem
        :key="currentItem?.id ?? 'loading'"
        :item="currentItem"
        :listing="selectionDetail.listing"
        :listing-error="selectionDetail.error"
        :error="selection.error"
        @select="openSelectedItem"
      />
      <template #cta>
        <button
          v-if="currentItem"
          type="button"
          class="listing-map-card__cta"
          @click="openSelectedItem(currentItem)"
        >
          {{ t('listing-detail.view-home') }}
        </button>
      </template>
    </MapCard>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue';
import ListingMapItem from './ListingMapItem.vue';
import MapCard from './MapCard.vue';
import SvgIcon from './SvgIcon.vue';
import { toApiParams } from '../lib/apiParams';
import { filterConfig } from '../lib/filterConfig';
import { circleBounds, circlePolygon } from '../lib/geoCircle';
import { t } from '../lib/i18n';
import {
  MARKER_COLOR,
  MARKER_IMAGE_ID,
  PIN_BODY_PATH,
  PIN_VIEWBOX,
  PIN_W_PATH,
  SELECTED_MARKER_IMAGE_ID,
  SELECTED_PIN_OFFSET,
  SELECTED_UPCOMING_MARKER_IMAGE_ID,
  UPCOMING_MARKER_CLIP_ID,
  UPCOMING_MARKER_EDGE_WIDTH,
  UPCOMING_MARKER_FILL,
  UPCOMING_MARKER_IMAGE_ID,
  addMarkerImages,
  addUpcomingMarkerImages
} from '../lib/mapPin';
import { CLUSTER_TOOLTIP_OFFSET, PIN_TOOLTIP_OFFSET, createMapTooltip, tooltipContent } from '../lib/mapTooltip';
import { createSearchApi } from '../lib/searchApi';
import { useCriteria } from '../lib/useCriteria';
import { useDetail } from '../lib/useDetail';
import { useMapFullscreen } from '../lib/useMapFullscreen';
import { useMapState } from '../lib/useMapState';
import { useProjectDetail } from '../lib/useProjectDetail';
import { useResults } from '../lib/useResults';
import { useSelectionCard } from '../lib/useSelectionCard';

// Biased towards the Brabant/Gelderland work area instead of the national
// view ProjectMapController uses; edges of Zeeland and the far east may fall off
const INITIAL_CENTER = [5.6, 51.95];
const INITIAL_ZOOM = 7.1;
const MAX_ZOOM = 13;

const PROJECTS_SOURCE = 'projects';
const RADIUS_SOURCE = 'search-radius';
const SELECTED_LAYER = 'project-selected';
// Projects without phases: their own unclustered source, so the clusters keep
// summing homes only and their counts stay equal to the result count
const UPCOMING_SOURCE = 'upcoming-projects';
const UPCOMING_LAYER = 'upcoming-points';
const UPCOMING_SELECTED_LAYER = 'upcoming-selected';
// Inverted cluster: white body with a red edge, so shape and fill both separate
// "a group, zoom in" from "one place" (WHN-2132).
const CLUSTER_FILL = '#ffffff';
const CLUSTER_STROKE = MARKER_COLOR;
// The map style is light, so a white outline barely registers; a dark shadow
// under the cluster is what actually lifts it off the background.
const SHADOW_OPACITY = 0.35;
const MARKER_SHADOW = `rgba(0, 0, 0, ${SHADOW_OPACITY})`;
// The search radius keeps its own blue: it is the area the user set, not a
// marker, and it must stay distinguishable from them.
const RADIUS_COLOR = '#008cba';
const RADIUS_FIT_PADDING = 40;
const MARKER_FIT_PADDING = 48;
// Well below MAX_ZOOM: re-framing after "Filters wissen" should show the spread
// of the results, not dive into one of them.
const MARKER_FIT_MAX_ZOOM = 11;
const EMPTY_COLLECTION = { type: 'FeatureCollection', features: [] };

// Funda-rule for clusters: a handful of markers that only separate at street
// level is faster to browse in the card than to zoom into. Tuned with the PO
// on the live data.
const SHALLOW_CLUSTER = { maxMarkers: 4, minExpansionZoom: 11 };
// maplibre's default is 10 leaves, which would silently truncate the card
const CLUSTER_LEAF_LIMIT = 100;
// Below this container width the card spans the map and covers the zoom
// controls, which then move up above it. Wider maps leave room beside the card
// and keep the controls where they are, so hovering pins does not bounce them.
const NARROW_MAP_WIDTH = 480;

const { criteria, update } = useCriteria();
const { open: openDetail, close: closeDetail, state: detailState } = useDetail();
const { open: openProjectDetail, state: projectDetailState } = useProjectDetail();
const { results } = useResults();
const { bbox, setBbox, fitRequest } = useMapState();

const api = createSearchApi();
const rangeBounds = {
  m2: [filterConfig.bounds.m2.min, filterConfig.bounds.m2.max],
  perceel: [filterConfig.bounds.perceel.min, filterConfig.bounds.perceel.max]
};

// maplibre-gl comes from the CDN include in overview.html.twig; the map column
// stays usable as the old placeholder when that include fails to load
const mapUnavailable = ref(false);

const container = ref(null);

const legendOpen = ref(false);

let map = null;
let resizeObserver = null;
let centerMarker = null;
let radiusLabel = null;
const tooltip = createMapTooltip(() => map);

const narrowMap = ref(false);

// The card shows one home at a time and browses through the homes of the
// selected projects (revised 2026-09-22). The entries are the projects from
// the marker properties ({id, name, city, count}), one per pin or several for
// a cluster; the homes behind them arrive from the marker endpoint below, so
// the card opens on a skeleton and the counter only shows once they are in.
const {
  selection,
  hasSelection,
  currentItem,
  card,
  cardStyle,
  openSelection,
  closeSelection,
  onCardClose,
  step,
  onCardResize,
  prefetchNeighbours,
  centerOnMarker,
  revealClickedPin
} = useSelectionCard({
  container,
  detailState,
  getMap: () => map,
  withProgrammaticMove: (fn) => withProgrammaticMove(fn),
  itemsFor: () => []
});

const { fullscreen, toggleFullscreen } = useMapFullscreen({
  getMap: () => map,
  canClose: () => !hasSelection.value && !detailState.isOpen && !projectDetailState.isOpen
});

const selectionDetail = reactive({ listing: null, error: false });

const openSelectedItem = (item) => openDetail(item);

// The homes follow the filters, like the marker count does: a filter change
// while the card is open refetches (the cache key carries the criteria). The
// bbox is not part of it, so the click-zoom leaves the set alone.
const markerQuery = computed(() => toApiParams(criteria, rangeBounds).toString());

let itemsRequest = 0;

watch([() => selection.entries, markerQuery], async ([projects]) => {
  itemsRequest += 1;
  const token = itemsRequest;
  const shownId = currentItem.value?.id ?? null;
  selection.items = [];
  selection.error = false;
  if (!projects.length) return;

  try {
    const responses = await Promise.all(
      projects.map((project) => api.markerItems(project.id, criteria, rangeBounds))
    );
    if (token !== itemsRequest) return;
    // Project after project, in the endpoint's order (available first, then
    // cheapest); project and city ride along for the card's subline
    selection.items = responses.flatMap(({ items }, index) =>
      items.map((item) => ({
        ...item,
        projectId: projects[index].id,
        project: projects[index].name,
        city: projects[index].city
      }))
    );
    // A filter change can leave the selected projects without a single home
    // (the pin disappears with them); a card with nothing to show closes
    if (!selection.items.length) {
      closeSelection();
      return;
    }
    // A filter change keeps the home you were looking at while it is still in
    const shownIndex = selection.items.findIndex((item) => item.id === shownId);
    selection.index = shownIndex === -1 ? 0 : shownIndex;
  } catch {
    if (token !== itemsRequest) return;
    selection.error = true;
  }
});

// Photo per home; the marker payload does not carry it. Cached in
// searchApi, so browsing back is instant.
let detailRequest = 0;

watch(currentItem, async (item) => {
  detailRequest += 1;
  const token = detailRequest;
  selectionDetail.listing = null;
  selectionDetail.error = false;
  if (!item) return;

  try {
    const listing = await api.detail(item.id);
    if (token !== detailRequest) return;
    selectionDetail.listing = listing;
    prefetchNeighbours((next) => api.detail(next.id));
  } catch {
    if (token !== detailRequest) return;
    selectionDetail.error = true;
  }
});

const syncSelectedPin = () => {
  if (!map || !map.getLayer(SELECTED_LAYER)) return;
  map.setFilter(SELECTED_LAYER, [
    'all',
    ['!', ['has', 'point_count']],
    ['==', ['get', 'projectId'], currentItem.value?.projectId ?? -1]
  ]);
};

watch(currentItem, syncSelectedPin);

const hasLocation = () => criteria.long !== null && criteria.lat !== null;

// moveend cannot tell a user gesture from fitBounds/easeTo (originalEvent is
// unreliable with inertia), so every programmatic camera move raises this flag
// and the moveend handler consumes it instead of writing a bbox
let programmaticMove = false;

// Raised by a re-frame request from outside (WHN-2127) and consumed once the
// matching result set has landed.
let pendingFit = false;

const withProgrammaticMove = (fn) => {
  programmaticMove = true;
  fn();
};

const onMoveEnd = () => {
  if (programmaticMove) {
    programmaticMove = false;
    return;
  }
  const bounds = map.getBounds();
  setBbox([bounds.getWest(), bounds.getSouth(), bounds.getEast(), bounds.getNorth()]);
};

const toGeoJson = (markers) => ({
  type: 'FeatureCollection',
  features: markers.map((marker) => ({
    type: 'Feature',
    geometry: { type: 'Point', coordinates: [marker.long, marker.lat] },
    properties: {
      projectId: marker.projectId,
      name: marker.name,
      city: marker.city,
      count: marker.count
    }
  }))
});

const toUpcomingGeoJson = (markers) => ({
  type: 'FeatureCollection',
  features: markers.map((marker) => ({
    type: 'Feature',
    geometry: { type: 'Point', coordinates: [marker.long, marker.lat] },
    properties: { projectId: marker.projectId, name: marker.name, city: marker.city }
  }))
});

const syncSelectedUpcomingPin = () => {
  if (!map || !map.getLayer(UPCOMING_SELECTED_LAYER)) return;
  const projectId = projectDetailState.isOpen ? projectDetailState.seed?.id ?? -1 : -1;
  map.setFilter(UPCOMING_SELECTED_LAYER, ['==', ['get', 'projectId'], projectId]);
};

watch(() => [projectDetailState.isOpen, projectDetailState.seed], syncSelectedUpcomingPin);

const markerItem = ({ properties }) => ({
  id: properties.projectId,
  name: properties.name,
  city: properties.city,
  count: properties.count
});

const addRadiusLayers = () => {
  map.addSource(RADIUS_SOURCE, { type: 'geojson', data: EMPTY_COLLECTION });
  map.addLayer({
    id: 'search-radius-fill',
    type: 'fill',
    source: RADIUS_SOURCE,
    paint: { 'fill-color': RADIUS_COLOR, 'fill-opacity': 0.08 }
  });
  map.addLayer({
    id: 'search-radius-line',
    type: 'line',
    source: RADIUS_SOURCE,
    paint: { 'line-color': RADIUS_COLOR, 'line-width': 2 }
  });
};

const updateRadiusCircle = () => {
  const source = map.getSource(RADIUS_SOURCE);
  if (!source) return;

  if (centerMarker) {
    centerMarker.remove();
    centerMarker = null;
  }
  if (radiusLabel) {
    radiusLabel.remove();
    radiusLabel = null;
  }

  if (!hasLocation() || criteria.radius === null) {
    source.setData(EMPTY_COLLECTION);
    return;
  }

  source.setData({
    type: 'Feature',
    geometry: circlePolygon(criteria.long, criteria.lat, criteria.radius),
    properties: {}
  });

  const element = document.createElement('div');
  element.className = 'marker-search-location';
  centerMarker = new window.maplibregl.Marker({ element, anchor: 'center' })
    .setLngLat([criteria.long, criteria.lat])
    .addTo(map);

  // Label floats on the circle's northernmost point: radius + search term
  const labelElement = document.createElement('div');
  labelElement.className = 'listing-map-radius-label';
  const labelText = document.createElement('span');
  labelText.textContent = criteria.loc
    ? `${criteria.radius} km · ${criteria.loc}`
    : `${criteria.radius} km`;
  const labelClear = document.createElement('button');
  labelClear.type = 'button';
  labelClear.className = 'listing-map-radius-label__clear';
  labelClear.setAttribute('aria-label', t('listing-map.radius-clear'));
  labelClear.textContent = '×';
  labelClear.addEventListener('click', (event) => {
    event.stopPropagation();
    update({ radius: null });
  });
  labelElement.append(labelText, labelClear);
  const topLatitude = circleBounds(criteria.long, criteria.lat, criteria.radius)[3];
  radiusLabel = new window.maplibregl.Marker({ element: labelElement, anchor: 'bottom', offset: [0, -6] })
    .setLngLat([criteria.long, topLatitude])
    .addTo(map);
};

const fitRadius = (animate) =>
  withProgrammaticMove(() =>
    map.fitBounds(circleBounds(criteria.long, criteria.lat, criteria.radius), {
      padding: RADIUS_FIT_PADDING,
      animate
    })
  );

const hasRadius = () => hasLocation() && criteria.radius !== null;

// Frames every marker of the current result set. maxZoom keeps a single result
// from slamming the camera to street level; the fit runs behind the programmatic
// flag so re-framing never writes a bbox and re-filters the list it just freed.
const fitToMarkers = () => {
  const positioned = [...results.markers, ...results.upcomingMarkers].filter(
    (marker) => marker.long !== null && marker.lat !== null
  );
  if (!positioned.length) return;

  const bounds = positioned.reduce(
    (extent, marker) => extent.extend([marker.long, marker.lat]),
    new window.maplibregl.LngLatBounds(
      [positioned[0].long, positioned[0].lat],
      [positioned[0].long, positioned[0].lat]
    )
  );

  withProgrammaticMove(() =>
    map.fitBounds(bounds, { padding: MARKER_FIT_PADDING, maxZoom: MARKER_FIT_MAX_ZOOM })
  );
};

// An active radius is the search area the user drew, so it has to stay fully in
// view; the marker extent would zoom past it, because every result lies inside
// the circle by definition (the radius filters server-side).
const reframe = () => (hasRadius() ? fitRadius(true) : fitToMarkers());

// Added before the listing layers so the pins with homes draw on top.
const addUpcomingLayers = () => {
  map.addSource(UPCOMING_SOURCE, { type: 'geojson', data: toUpcomingGeoJson(results.upcomingMarkers) });
  map.addLayer({
    id: UPCOMING_LAYER,
    type: 'symbol',
    source: UPCOMING_SOURCE,
    layout: {
      'icon-image': UPCOMING_MARKER_IMAGE_ID,
      'icon-anchor': 'bottom',
      'icon-allow-overlap': true
    }
  });
  map.addLayer({
    id: UPCOMING_SELECTED_LAYER,
    type: 'symbol',
    source: UPCOMING_SOURCE,
    filter: ['==', ['get', 'projectId'], -1],
    layout: {
      'icon-image': SELECTED_UPCOMING_MARKER_IMAGE_ID,
      'icon-anchor': 'bottom',
      'icon-offset': [0, SELECTED_PIN_OFFSET],
      'icon-allow-overlap': true,
      'icon-ignore-placement': true
    }
  });
  syncSelectedUpcomingPin();

  // maplibre fires an event for every layer under the pointer, so where a
  // listing pin or cluster overlaps (drawn on top) only that one may respond
  const coveredByListingMarker = (point) =>
    map.queryRenderedFeatures(point, { layers: ['project-points', 'project-clusters', SELECTED_LAYER] }).length > 0;

  map.on('mousemove', UPCOMING_LAYER, (event) => {
    if (coveredByListingMarker(event.point)) return;
    const feature = event.features[0];
    tooltip.show(
      `upcoming:${feature.properties.projectId}`,
      feature.geometry.coordinates,
      tooltipContent({ title: feature.properties.name, hint: t('listing-map.pin.hint') }),
      PIN_TOOLTIP_OFFSET
    );
  });
  map.on('mouseleave', UPCOMING_LAYER, () => tooltip.hide('upcoming:'));

  // The project opens in its own sidebar, as on the projects page; the
  // listing card and panel close first so only one sidebar is ever open
  map.on('click', UPCOMING_LAYER, (event) => {
    if (coveredByListingMarker(event.point)) return;
    const { projectId, name, city } = event.features[0].properties;
    tooltip.hide();
    closeSelection();
    if (detailState.isOpen) closeDetail();
    openProjectDetail({ id: projectId, project: name, city });
  });
};

const addProjectLayers = () => {
  map.addSource(PROJECTS_SOURCE, {
    type: 'geojson',
    data: toGeoJson(results.markers),
    cluster: true,
    clusterRadius: 35,
    // Clusters sum the homes per project, matching the toolbar's result count;
    // the project count only feeds the hover tooltip
    clusterProperties: { homeCount: ['+', ['get', 'count']], projectCount: ['+', 1] }
  });
  // Shadow under the cluster: the previous translucent halo in the cluster color
  // is now a dark offset circle, because on this light map style that is what
  // makes the white cluster body read as raised (WHN-2132).
  map.addLayer({
    id: 'project-cluster-shadow',
    type: 'circle',
    source: PROJECTS_SOURCE,
    filter: ['has', 'point_count'],
    paint: {
      'circle-color': MARKER_SHADOW,
      'circle-translate': [0, 1],
      'circle-radius': ['step', ['get', 'homeCount'], 17, 25, 21, 100, 27]
    }
  });
  map.addLayer({
    id: 'project-clusters',
    type: 'circle',
    source: PROJECTS_SOURCE,
    filter: ['has', 'point_count'],
    paint: {
      'circle-color': CLUSTER_FILL,
      'circle-stroke-color': CLUSTER_STROKE,
      'circle-stroke-width': 2,
      'circle-radius': ['step', ['get', 'homeCount'], 16, 25, 20, 100, 26]
    }
  });
  map.addLayer({
    id: 'project-cluster-counts',
    type: 'symbol',
    source: PROJECTS_SOURCE,
    filter: ['has', 'point_count'],
    layout: {
      'text-field': ['to-string', ['get', 'homeCount']],
      // Bold is available in the OpenFreeMap glyph set, checked against
      // /fonts/Noto%20Sans%20Bold/0-255.pbf
      'text-font': ['Noto Sans Bold'],
      'text-size': 12,
      // The project pins occupy the symbol collision index; without this the
      // count on a nearby cluster gets culled until the pins move away on zoom
      'text-allow-overlap': true
    },
    paint: { 'text-color': CLUSTER_STROKE }
  });
  map.addLayer({
    id: 'project-points',
    type: 'symbol',
    source: PROJECTS_SOURCE,
    filter: ['!', ['has', 'point_count']],
    layout: {
      'icon-image': MARKER_IMAGE_ID,
      'icon-anchor': 'bottom',
      'icon-allow-overlap': true
    }
    // No number on the pin: the W fills the head, and how many homes sit here
    // is what the card answers. The clusters keep their own count, because
    // those group locations you cannot see apart yet.
  });
  // The selected pin, drawn over the plain one so the rim shows; the filter is
  // set from the selection and matches nothing until a pin is selected
  map.addLayer({
    id: SELECTED_LAYER,
    type: 'symbol',
    source: PROJECTS_SOURCE,
    filter: ['all', ['!', ['has', 'point_count']], ['==', ['get', 'projectId'], -1]],
    layout: {
      'icon-image': SELECTED_MARKER_IMAGE_ID,
      'icon-anchor': 'bottom',
      'icon-offset': [0, SELECTED_PIN_OFFSET],
      'icon-allow-overlap': true,
      'icon-ignore-placement': true
    }
  });
  syncSelectedPin();

  // Zoom, or open the card: see ProjectMap for the three outcomes
  map.on('click', 'project-clusters', async (event) => {
    const feature = event.features[0];
    const { cluster_id: clusterId, point_count: markerCount } = feature.properties;
    tooltip.hide();
    const source = map.getSource(PROJECTS_SOURCE);
    const zoom = await source.getClusterExpansionZoom(clusterId);
    const isShallow =
      markerCount <= SHALLOW_CLUSTER.maxMarkers && zoom > SHALLOW_CLUSTER.minExpansionZoom;

    // Not wrapped in withProgrammaticMove: zooming into a cluster is a user
    // gesture, so the camera move must update the bbox and the listing. The
    // card branch below only centers, flagged programmatic (see centerOnMarker)
    if (!isShallow && zoom <= MAX_ZOOM) {
      map.easeTo({ center: feature.geometry.coordinates, zoom });
      return;
    }

    const leaves = await source.getClusterLeaves(clusterId, CLUSTER_LEAF_LIMIT, 0);
    openSelection(leaves.map(markerItem));
    centerOnMarker(feature.geometry.coordinates);
  });

  // mousemove instead of mouseenter: the layer stays "entered" when the cursor
  // slides from one marker straight onto a neighbouring one
  map.on('mousemove', 'project-clusters', (event) => {
    const feature = event.features[0];
    const { cluster_id: clusterId, homeCount, projectCount } = feature.properties;
    tooltip.show(
      `cluster:${clusterId}`,
      feature.geometry.coordinates,
      tooltipContent({
        title: t('listing-map.cluster.summary', { count: homeCount, projects: projectCount }),
        hint: t('listing-map.cluster.zoom')
      }),
      CLUSTER_TOOLTIP_OFFSET
    );
  });
  map.on('mouseleave', 'project-clusters', () => tooltip.hide('cluster:'));

  // A selected project's pin gets no tooltip: the card already shows it
  map.on('mousemove', 'project-points', (event) => {
    const feature = event.features[0];
    if (selection.entries.some((project) => project.id === feature.properties.projectId)) {
      tooltip.hide();
      return;
    }
    tooltip.show(
      `pin:${feature.properties.projectId}`,
      feature.geometry.coordinates,
      pinTooltipContent(feature.properties),
      PIN_TOOLTIP_OFFSET
    );
  });
  map.on('mouseleave', 'project-points', () => tooltip.hide('pin:'));

  map.on('click', 'project-points', (event) => {
    const feature = event.features[0];
    tooltip.hide();
    openSelection([markerItem(feature)]);
    revealClickedPin(feature.geometry.coordinates, event.point);
  });

  map.on('click', (event) => {
    if (!hasSelection.value) return;
    const hits = map.queryRenderedFeatures(event.point, { layers: ['project-points'] });
    if (!hits.length) closeSelection();
  });

  // One handler for every clickable layer: per-layer enter/leave pairs reset
  // each other's cursor where pins overlap
  const clickableLayers = ['project-points', 'project-clusters', UPCOMING_LAYER];
  map.on('mousemove', (event) => {
    const overMarker = map.queryRenderedFeatures(event.point, { layers: clickableLayers }).length > 0;
    map.getCanvas().style.cursor = overMarker ? 'pointer' : '';
  });
};

const pinTooltipContent = ({ count }) =>
  tooltipContent({
    title: count === 1 ? t('listing-map.popup.home') : t('listing-map.popup.homes', { count }),
    hint: t('listing-map.pin.hint')
  });

// MapLibre keeps the compact attribution expanded until the first drag and
// re-expands it on every resize; collapse it to the (i) button ourselves
const collapseAttribution = () => {
  container.value
    .querySelectorAll('.maplibregl-ctrl-attrib.maplibregl-compact-show')
    .forEach((attribution) => {
      attribution.setAttribute('open', '');
      attribution.classList.remove('maplibregl-compact-show');
    });
};

// The OpenFreeMap style labels in the local language; prefer the Dutch name
// wherever the style renders a plain name (same override as the /aanbod map)
const applyDutchLabels = () => {
  map.getStyle().layers.forEach((layer) => {
    if (!layer.layout || !layer.layout['text-field']) return;
    if (JSON.stringify(layer.layout['text-field']).includes('"name"')) {
      map.setLayoutProperty(layer.id, 'text-field', ['coalesce', ['get', 'name:nl'], ['get', 'name']]);
    }
  });
};

onMounted(() => {
  if (!window.maplibregl) {
    mapUnavailable.value = true;
    return;
  }

  map = new window.maplibregl.Map({
    container: container.value,
    style: 'https://tiles.openfreemap.org/styles/bright',
    center: INITIAL_CENTER,
    zoom: INITIAL_ZOOM,
    maxZoom: MAX_ZOOM,
    attributionControl: { compact: true },
    // maplibre's own ResizeObserver would call resize() outside the
    // programmaticMove flag below, and that moveend writes a bbox: a search
    // request on every header or column resize. The ResizeObserver in
    // onMounted does the resizing, flagged.
    trackResize: false,
    cooperativeGestures: true,
    dragRotate: false,
    touchPitch: false,
    locale: {
      'CooperativeGesturesHandler.WindowsHelpText': t('listing-map.zoom-hint'),
      'CooperativeGesturesHandler.MacHelpText': t('listing-map.zoom-hint-mac'),
      'CooperativeGesturesHandler.MobileHelpText': t('listing-map.zoom-hint-mobile')
    }
  });
  map.touchZoomRotate.disableRotation();
  map.keyboard.disableRotation();
  map.addControl(new window.maplibregl.NavigationControl({ showCompass: false }), 'bottom-right');
  // Defaults zoom in tiny increments: mouse wheel 1/450, trackpad pinch 1/100
  map.scrollZoom.setWheelZoomRate(1 / 75);
  map.scrollZoom.setZoomRate(1 / 25);
  map.on('load', async () => {
    applyDutchLabels();
    addRadiusLayers();
    await Promise.all([addMarkerImages(() => map), addUpcomingMarkerImages(() => map)]);
    if (!map) return;
    addUpcomingLayers();
    addProjectLayers();
    updateRadiusCircle();
    collapseAttribution();
    // Registered after load: maplibre fires a moveend while settling the
    // initial camera, which must not register as a user pan and write a bbox
    // (that silently narrows the list to the initial viewport on page load).
    map.on('moveend', onMoveEnd);
  });
  map.on('resize', collapseAttribution);

  // Camera priority, here and in reframe(): a bbox from a real user gesture
  // wins, then an active radius, then the extent of the results, and failing all
  // three the default center/zoom this map was constructed with.
  //
  // Remount (list <-> both/map switch) restores the viewport the list is
  // currently filtered on; without a bbox an active location defines the view
  if (bbox.value) {
    withProgrammaticMove(() => map.fitBounds(bbox.value, { animate: false }));
  } else if (hasRadius()) {
    fitRadius(false);
  }

  watch(
    () => [criteria.long, criteria.lat, criteria.radius],
    () => {
      // A location filter defines the view itself, so it takes the camera back
      // from a pending re-frame request.
      pendingFit = false;
      updateRadiusCircle();
      if (hasRadius()) {
        fitRadius(true);
        return;
      }
      // Location or radius switched off: nothing defines the view any more and
      // the results just went national, so re-frame on them once they land
      // (WHN-2128).
      pendingFit = true;
    }
  );

  watch(
    () => results.markers,
    (markers) => {
      const source = map.getSource(PROJECTS_SOURCE);
      if (source) source.setData(toGeoJson(markers));
      // Re-frame only once the fresh markers are in: the request arrives with
      // the filter change, the new result set a debounce later.
      if (pendingFit) {
        pendingFit = false;
        reframe();
      }
    }
  );

  watch(
    () => results.upcomingMarkers,
    (markers) => {
      const source = map.getSource(UPCOMING_SOURCE);
      if (source) source.setData(toUpcomingGeoJson(markers));
    }
  );

  watch(fitRequest, () => {
    pendingFit = true;
  });

  // The both<->map view switch resizes the column without a window resize.
  // maplibre's resize() fires its own moveend (synchronously), so it runs
  // behind the programmatic flag: a container resize must never register as a
  // user pan and write a bbox (that would fire an unwanted search request).
  // The flag is cleared right after because a no-op resize fires nothing and
  // a lingering flag would swallow the next real pan.
  // resize() also empties the canvas and defers the repaint to the next frame;
  // the collapsing toolbar resizes the column every frame of its transition,
  // so without a synchronous redraw the map blinks white while it animates.
  resizeObserver = new ResizeObserver(() => {
    programmaticMove = true;
    map.resize();
    programmaticMove = false;
    map.redraw();
    narrowMap.value = container.value.clientWidth < NARROW_MAP_WIDTH;
  });
  resizeObserver.observe(container.value);
});

onBeforeUnmount(() => {
  if (resizeObserver) resizeObserver.disconnect();
  tooltip.hide();
  if (map) map.remove();
  map = null;
});
</script>
